/**
 * R77 · Packages module view-mode hook.
 * ----------------------------------------------------------------
 * Two modes:
 *   • "delivery"  — no rates/amounts/costs/margins anywhere.
 *   • "financial" — every column the caller is allowed to see.
 *
 * Rules (per user + system prompt):
 *   • If the caller has NO finance perm, they are locked to "delivery"
 *     and the toggle is completely hidden (user's option 1a).
 *   • If the caller has ANY finance perm, they see the toggle and
 *     default to "financial". Their choice is remembered per-user
 *     globally in localStorage (option 2a).
 *   • We continue to use the existing finance perms (option 3a);
 *     no new dedicated `package.mode.*` codes.
 *
 * The backend `_finance_flags` still redacts every money field regardless
 * of what the client says — this hook is UX polish, not a security gate.
 */
import { useCallback, useEffect, useState } from "react";
import useWorkspacePerms from "@/lib/useWorkspacePerms";

const FINANCE_PERMS = [
  "package.view_rates", "package.view_costs", "package.view_margins",
  "brand.finance.view_rates", "brand.finance.view_costs",
  "brand.finance.view_profitability", "brand.finance.view_billing",
  "brand.finance.manage",
];

const MANAGE_PERMS = [
  "package.manage", "activity_master.manage", "rate_card.manage",
  "package.templates.manage",
];

// Feb 2026 · Per-subtab permission codes (see routes_roles migration v11).
// Workspace admins and any role with `package.manage` bypass these checks.
const SUBTAB_PERMS = {
  dashboard:  ["package.dashboard.view"],
  templates:  ["package.templates.view", "package.templates.manage"],
  planner:    ["package.planner.view"],
  master:     ["activity_master.view", "activity_master.manage"],
  rates:      ["rate_card.view", "rate_card.manage"],
  intel:      ["package.baselines.view"],
  allocation: ["package.allocation.view"],
};

const LS_KEY = "co_pkg_view_mode";

export function usePackageMode() {
  const { can, ready, isAdminRole } = useWorkspacePerms();
  const canFinance = FINANCE_PERMS.some((p) => can(p));
  const canManage = MANAGE_PERMS.some((p) => can(p));
  const isBypass = isAdminRole || can("package.manage");
  const canTab = (key) => isBypass || (SUBTAB_PERMS[key] || []).some((p) => can(p));

  const [mode, setModeState] = useState(() => {
    try {
      const saved = localStorage.getItem(LS_KEY);
      if (saved === "delivery" || saved === "financial") return saved;
    } catch { /* ignore */ }
    return "financial";  // default; will be forced to delivery below if no perm
  });

  // Once perms are ready, force delivery mode for users without finance perms.
  useEffect(() => {
    if (!ready) return;
    if (!canFinance && mode !== "delivery") {
      setModeState("delivery");
    }
  }, [ready, canFinance, mode]);

  const setMode = useCallback((next) => {
    if (next !== "delivery" && next !== "financial") return;
    // Guard: cannot switch to financial without perms.
    if (next === "financial" && !canFinance) return;
    setModeState(next);
    try { localStorage.setItem(LS_KEY, next); } catch { /* ignore */ }
  }, [canFinance]);

  const toggle = useCallback(() => {
    setMode(mode === "delivery" ? "financial" : "delivery");
  }, [mode, setMode]);

  const canDashboard  = canTab("dashboard");
  const canTemplates  = canTab("templates");
  const canPlanner    = canTab("planner");
  const canMaster     = canTab("master");
  const canRates      = canTab("rates") || canFinance;   // finance perms transitively grant rate-card visibility
  const canIntel      = canTab("intel") || canFinance;   // baselines are inherently financial
  const canAllocation = canTab("allocation");

  return {
    ready,
    mode,
    setMode,
    toggle,
    canFinance,           // may see any money field
    canManage,            // may edit master / rate card / templates
    canToggle: canFinance,// UI: show the pill switch?
    // Convenience booleans — canonical way to gate money UI in packages:
    showMoney: canFinance && mode === "financial",
    // Feb 2026 · Per-subtab visibility flags. Any of these true → the row of
    // secondary tabs is shown. `canSeeSubtabs` is kept for legacy callers.
    canDashboard,
    canTemplates,
    canPlanner,
    canMaster,
    canRates,
    canIntel,
    canAllocation,
    canSeeSubtabs: (canDashboard || canTemplates || canPlanner || canMaster
                     || canRates || canIntel || canAllocation),
  };
}

export default usePackageMode;
